import { IState, IFilterValue } from './state';
import { ActionTree, Store, ActionContext } from 'vuex';
import * as api from '../api';

type PayloadType =
	| 'initApp'
	| 'setState'
	| 'setQuery'
	| 'search'
	| 'setFilterOpen'
	| 'toggleFilterValue'
	| 'setPage'
	| 'clearAllFilters';
export type ActionPayload<ActionData> = {
	type: PayloadType;
	payload: ActionData;
};

type ActionHandler<ActionData> = (
	this: Store<IState>,
	injectee: ActionContext<IState, IState>,
	payload: ActionPayload<ActionData>
) => any;
type ActionCreator<ActionData> = (
	data: ActionData
) => ActionPayload<ActionData>;

// Action creators
export const initApp: ActionCreator<IState> = state => ({
	type: 'initApp',
	payload: state
});

const initAppAction: ActionHandler<IState> = ({ commit }, { payload }) => {
	commit({
		type: 'initApp',
		payload
	});
};

export const search: ActionCreator<undefined> = (payload = undefined) => ({
	type: 'search',
	payload
});

const searchAction: ActionHandler<undefined> = ({ commit, state, getters }) => {
	if (!state.isFetching) {
		commit({
			type: 'setState',
			payload: {
				isFetching: true
			}
		});
		const dates = (getters.activeDates as IFilterValue[])
			.map(v => v.value);

		const tags = (getters.activeTags as IFilterValue[])
			.map(v => v.value);

		api.search(state.searchUrl, {
			page: state.pagination.current,
			tags,
			dates
		})
			.then(result => {
				commit({
					type: 'setState',
					payload: {
						posts: result.posts,
						pagination: result.pagination,
						total: result.total,
						isFetching: false
					}
				});
			})
			.catch(() => {
				commit({
					type: 'setState',
					payload: {
						isFetching: false
					}
				});
			});
	}
};

export const setPage: ActionCreator<number> = page => ({
	type: 'setPage',
	payload: page
});

const setPageAction: ActionHandler<number> = (
	{ commit, dispatch, state },
	{ payload }
) => {
	commit({
		type: 'setPage',
		payload: payload
	});
	// Search
	dispatch(search(undefined));
};

export const setQuery: ActionCreator<string> = payload => ({
	type: 'setQuery',
	payload
});

const setQueryAction: ActionHandler<string> = (
	{ commit, dispatch, state },
	{ payload }
) => {
	commit({
		type: 'setPage',
		payload: 0
	});

	commit({
		type: 'setState',
		payload: {
			filters: {
				...state.filters,
				query: payload
			}
		}
	});

	dispatch(search(undefined));
};

export interface ISetFilterOpenPayload {
	name: string;
	open: boolean;
}

export const setFilterOpen: ActionCreator<ISetFilterOpenPayload> = payload => ({
	type: 'setFilterOpen',
	payload
});

const setFilterOpenAction: ActionHandler<ISetFilterOpenPayload> = (
	{ commit, getters },
	{ payload }
) => {
	if (payload.open) {
		// Open, close others
		const openFilter = getters.openFilterName;
		if (openFilter) {
			commit({
				type: 'setFilterOpen',
				payload: {
					name: openFilter,
					open: false
				}
			});
		}
	}
	// Set this filter open value
	commit({
		type: 'setFilterOpen',
		payload
	});
};

export const clearAllFilters: ActionCreator<IToggleFilterValuePayload | undefined> = payload => ({
	type: 'clearAllFilters',
	payload
});

const clearAllFiltersAction: ActionHandler<IToggleFilterValuePayload | undefined> = ({ state, commit, dispatch }, { payload }) => {
	state.selectedFilterValues.forEach(filter => {
		commit({
			type: 'toggleFilterValue',
			payload: {
				name: filter.filterName,
				value: filter
			}
		})
	})

	if(payload && !payload.value.selected) {
		commit({
			type: 'toggleFilterValue',
			payload
		})
	}

	commit({
		type: 'setPage',
		payload: 0
	});

	dispatch(search(undefined));
}

export interface IToggleFilterValuePayload {
	name: string;
	value: IFilterValue;
}
export const toggleFilterValue: ActionCreator<IToggleFilterValuePayload> = payload => ({
	type: 'toggleFilterValue',
	payload
});

const toggleFilterValueAction: ActionHandler<IToggleFilterValuePayload> = (
	{ commit, dispatch },
	{ payload }
) => {
	commit({
		type: 'toggleFilterValue',
		payload
	});

	commit({
		type: 'setPage',
		payload: 0
	});

	dispatch(search(undefined));
};

const actions: ActionTree<IState, IState> = {
	initApp: initAppAction,
	setPage: setPageAction,
	search: searchAction,
	setQuery: setQueryAction,
	setFilterOpen: setFilterOpenAction,
	toggleFilterValue: toggleFilterValueAction,
	clearAllFilters: clearAllFiltersAction
};

export default actions;
